import {Record as record} from 'immutable';
import _ from 'lodash';
import keyMirror from 'keymirror';

import {construct, fields} from '../helpers/LinkMapper';
import {getDefaults, getResolvers} from '../helpers/Records';
import BaseStore from './BaseStore';
import {ActionTypes, Pay360SessionState} from '../constants/AppConstants';
import trainTime from '../lib/TrainTime';

// Users need to complete their payment before the auth token expires (refresh not possible during hosted session)
// Guest users can have more time
const USER_SESSION_TIMEOUT = {minutes: 4, seconds: 30};
const GUEST_SESSION_TIMEOUT = {minutes: 10};

// Pay360 states, we'll map various combinations of these to a single Pay360SessionState
const Pay360 = keyMirror({
    TERMINATED: null,
    SUCCESS: null,
    STARTED: null,
    PROCESSING: null,
    FAILED: null,
    NOT_SUBMITTED: null,
});

const stateSchema = {
    redirectUrl: [fields.string],
    sessionId: [fields.string],
    sessionStatus: [fields.string],
    sessionCreated: [fields.datetime],
    sessionExpires: [fields.datetime],
    isGuestSession: [fields.bool],
};
const defaultState = getDefaults(stateSchema);

class Pay360State extends record(defaultState) {
    static fromJSON(json = {}) {
        return construct(Pay360State, _.defaults(json, defaultState), getResolvers(stateSchema));
    }
}

class Pay360Store extends BaseStore {
    getActionHandlers() {
        return {
            [ActionTypes.PAY360_SESSION_CREATE_STARTED]: this.handleSessionCreating,
            [ActionTypes.PAY360_SESSION_CREATED]: this.handleSessionCreated,
            [ActionTypes.PAY360_SESSION_LOADED]: this.handleSessionLoaded,
            [ActionTypes.PAYMENT_FORM_CHANGED]: this.resetState,
            [ActionTypes.ORDER_EXPIRED]: this.resetState,
            [ActionTypes.PAYMENT_LEFT]: this.resetState,
            [ActionTypes.LOGGED_OUT]: this.resetState,
        };
    }

    handleSessionCreating() {
        this.setState(new Pay360State({sessionStatus: Pay360SessionState.CREATING}));
    }

    handleSessionCreated({session, isGuest}) {
        const created = trainTime();
        const expires = created
            .clone()
            .add(session.isGuest ? GUEST_SESSION_TIMEOUT : USER_SESSION_TIMEOUT);

        this.setState(
            this.state.merge({
                redirectUrl: session.redirectUrl,
                sessionId: session.sessionId,
                sessionStatus: Pay360SessionState.READY,
                sessionCreated: created,
                sessionExpires: expires,
                isGuestSession: isGuest,
            }),
        );
    }

    handleSessionLoaded({session}) {
        let current = this.state.sessionStatus;
        if (session.status === Pay360.FAILED) {
            current = Pay360SessionState.INVALID;
        } else if (session.status === Pay360.SUCCESS) {
            const {
                sessionState,
                transactionState: {transactionState},
            } = session.hostedSessionStatus;

            if (sessionState === Pay360.TERMINATED) {
                if (transactionState === Pay360.SUCCESS) {
                    current = Pay360SessionState.SUCCESS;
                } else if (transactionState === Pay360.FAILED) {
                    current = Pay360SessionState.FAILED;
                } else if (transactionState === Pay360.NOT_SUBMITTED) {
                    current = Pay360SessionState.CANCELLED;
                }
            }
        }
        if (current !== this.state.sessionStatus) {
            this.setState(this.state.set('sessionStatus', current));
        }
    }
}

const storeInstance = new Pay360Store({
    storageKey: 'pay360',
    expires: 30 * 60,
    recordType: Pay360State,
});

export default storeInstance;
